import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  People,
  ChildCare,
  LocationOn,
  Download,
  MonetizationOn,
  EmojiEvents,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { getPartnerContributions, getMothersByPartner } from '../api';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const PartnerDisbursements = () => {
  const { partnerId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [disbursements, setDisbursements] = useState([]);
  const [mothers, setMothers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSensitiveData, setShowSensitiveData] = useState(false);

  // Color palette for charts
  const colors = ['#ff69b4', '#ff1493', '#ff8da1', '#ff6b9d', '#ff4d94'];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [disbursementsData, mothersData] = await Promise.all([
          getPartnerContributions(partnerId),
          getMothersByPartner(partnerId)
        ]);
        
        setDisbursements(Array.isArray(disbursementsData) ? disbursementsData : []);
        setMothers(mothersData.mothers || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [partnerId]);

  // Calculate key metrics
  const totalDisbursed = disbursements.reduce((sum, d) => sum + d.amount, 0);
  const mothersSupported = new Set(disbursements.map(d => d.mother_id)).size;
  const childrenReached = mothers.reduce((sum, m) => sum + (m.num_children || 0), 0);
  const regionsReached = new Set(mothers.map(m => m.location)).size;

  // Prepare monthly disbursement data
  const monthlyData = disbursements.reduce((acc, disbursement) => {
    const month = disbursement.month_key;
    if (!acc[month]) {
      acc[month] = { month, amount: 0, count: 0 };
    }
    acc[month].amount += disbursement.amount;
    acc[month].count += 1;
    return acc;
  }, {});

  const monthlyChartData = Object.values(monthlyData)
    .sort((a, b) => a.month.localeCompare(b.month))
    .map(item => ({
      ...item,
      month: new Date(item.month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
    }));

  // Prepare mothers funded data
  const mothersFundedData = mothers.map(mother => {
    const motherDisbursements = disbursements.filter(d => d.mother_id === mother.generated_id);
    const totalFunded = motherDisbursements.reduce((sum, d) => sum + d.amount, 0);
    return {
      name: `${mother.first_name} ${mother.surname}`,
      funded: totalFunded,
      children: mother.num_children || 0,
      location: mother.location
    };
  }).filter(m => m.funded > 0);

  // Prepare regional distribution data
  const regionalData = mothers.reduce((acc, mother) => {
    const location = mother.location || 'Unknown';
    if (!acc[location]) {
      acc[location] = { name: location, value: 0, mothers: 0 };
    }
    acc[location].mothers += 1;
    acc[location].value += mother.num_children || 0;
    return acc;
  }, {});

  const regionalChartData = Object.values(regionalData);

  const handleDownloadReport = (format) => {
    // This would integrate with a proper PDF/CSV generation library
    const reportData = {
      totalDisbursed,
      mothersSupported,
      childrenReached,
      regionsReached,
      disbursements,
      monthlyData: monthlyChartData,
      mothersFundedData
    };
    
    if (format === 'csv') {
      // Generate CSV
      const csvContent = generateCSV(reportData);
      downloadFile(csvContent, 'partner-disbursements-report.csv', 'text/csv');
    } else {
      // Generate PDF (placeholder)
      alert('PDF download functionality would be implemented with a library like jsPDF');
    }
  };

  const generateCSV = (data) => {
    const headers = ['Metric', 'Value'];
    const rows = [
      ['Total Amount Disbursed', data.totalDisbursed.toFixed(2)],
      ['Mothers Supported', data.mothersSupported],
      ['Children Reached', data.childrenReached],
      ['Regions Reached', data.regionsReached],
      ['', ''],
      ['Month', 'Amount', 'Disbursements'],
      ...data.monthlyData.map(item => [item.month, item.amount.toFixed(2), item.count])
    ];
    
    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  const downloadFile = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        bgcolor: '#f8f9fa'
      }}>
        <CircularProgress size={60} sx={{ color: '#ff69b4' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (disbursements.length === 0) {
    return (
      <Box sx={{ 
        minHeight: '100vh',
        bgcolor: '#f8f9fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4
      }}>
        <Paper sx={{ 
          p: 6, 
          textAlign: 'center', 
          borderRadius: 4,
          background: '#fff',
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
          border: '1px solid #e0e0e0'
        }}>
          <EmojiEvents sx={{ fontSize: 80, color: '#ff69b4', mb: 3 }} />
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 700, color: '#333' }}>
            No Disbursements Found
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: '#666', fontSize: '1.1rem' }}>
            Start funding mothers to see progress here.
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              background: 'linear-gradient(45deg, #ff69b4, #ff1493)',
              '&:hover': {
                background: 'linear-gradient(45deg, #ff5ba4, #ff0d8a)',
              }
            }}
          >
            Start Your First Disbursement
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: '#f8f9fa',
      p: isMobile ? 2 : 4
    }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h3" sx={{ 
          fontWeight: 700, 
          color: '#333',
          fontSize: isMobile ? '1.8rem' : '2.5rem'
        }}>
          Partner Disbursements Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Toggle sensitive data">
            <IconButton
              onClick={() => setShowSensitiveData(!showSensitiveData)}
              sx={{ 
                color: '#666', 
                bgcolor: '#fff',
                border: '1px solid #e0e0e0',
                '&:hover': { bgcolor: '#f5f5f5' }
              }}
            >
              {showSensitiveData ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={() => handleDownloadReport('csv')}
            sx={{
              bgcolor: '#ff69b4',
              color: '#fff',
              '&:hover': { bgcolor: '#ff1493' }
            }}
          >
            Download CSV
          </Button>
        </Box>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: 3,
            background: '#fff',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #f0f0f0',
            transition: 'all 0.3s ease',
            '&:hover': { 
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(0,0,0,0.12)'
            }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <MonetizationOn sx={{ fontSize: 40, color: '#ff69b4', mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 900, mb: 1, color: '#333' }}>
                UGX {totalDisbursed.toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>
                Total Amount Disbursed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: 3,
            background: '#fff',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #f0f0f0',
            transition: 'all 0.3s ease',
            '&:hover': { 
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(0,0,0,0.12)'
            }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <People sx={{ fontSize: 40, color: '#ff69b4', mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 900, mb: 1, color: '#333' }}>
                {mothersSupported}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>
                Mothers Supported
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: 3,
            background: '#fff',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #f0f0f0',
            transition: 'all 0.3s ease',
            '&:hover': { 
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(0,0,0,0.12)'
            }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <ChildCare sx={{ fontSize: 40, color: '#ff69b4', mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 900, mb: 1, color: '#333' }}>
                {childrenReached}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>
                Children Reached
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: 3,
            background: '#fff',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #f0f0f0',
            transition: 'all 0.3s ease',
            '&:hover': { 
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(0,0,0,0.12)'
            }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <LocationOn sx={{ fontSize: 40, color: '#ff69b4', mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 900, mb: 1, color: '#333' }}>
                {regionsReached}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>
                Regions Reached
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Monthly Disbursement Trends */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ 
            p: 3, 
            borderRadius: 3,
            background: '#fff',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #f0f0f0'
          }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#333' }}>
              Monthly Disbursement Trends
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" stroke="#666" />
                <YAxis stroke="#666" />
                <RechartsTooltip 
                  contentStyle={{ 
                    background: '#fff', 
                    border: '1px solid #e0e0e0',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="amount" 
                  stroke="#ff69b4" 
                  strokeWidth={3}
                  dot={{ fill: '#ff69b4', strokeWidth: 2, r: 6 }}
                  activeDot={{ r: 8, stroke: '#ff1493', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Regional Distribution */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ 
            p: 3, 
            borderRadius: 3,
            background: '#fff',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #f0f0f0'
          }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#333' }}>
              Regional Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={regionalChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {regionalChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                  ))}
                </Pie>
                <RechartsTooltip 
                  contentStyle={{ 
                    background: '#fff', 
                    border: '1px solid #e0e0e0',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Mothers Funded Chart */}
      <Paper sx={{ 
        p: 3, 
        mb: 4,
        borderRadius: 3,
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(20px)',
        boxShadow: '0 15px 35px rgba(0,0,0,0.1)'
      }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#333' }}>
          Mothers Funded
        </Typography>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={mothersFundedData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="name" stroke="#666" angle={-45} textAnchor="end" height={100} />
            <YAxis stroke="#666" />
            <RechartsTooltip 
              contentStyle={{ 
                background: 'rgba(255,255,255,0.95)', 
                border: '1px solid #ff69b4',
                borderRadius: 8
              }}
            />
            <Bar dataKey="funded" fill="#ff69b4" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      {/* Transaction History */}
      <Paper sx={{ 
        p: 3, 
        borderRadius: 3,
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(20px)',
        boxShadow: '0 15px 35px rgba(0,0,0,0.1)'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
            Transaction History
          </Typography>
          <Chip 
            label={`${disbursements.length} transactions`} 
            color="primary" 
            sx={{ bgcolor: '#ff69b4', color: '#fff' }}
          />
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(255, 105, 180, 0.1)' }}>
                <TableCell sx={{ fontWeight: 700 }}>Mother ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Month</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Amount (UGX)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {disbursements.slice(0, 10).map((disbursement) => (
                <TableRow key={disbursement.id} sx={{ '&:hover': { bgcolor: 'rgba(255, 105, 180, 0.05)' } }}>
                  <TableCell>
                    {showSensitiveData ? disbursement.mother_id : '***' + disbursement.mother_id.slice(-4)}
                  </TableCell>
                  <TableCell>{disbursement.month_key}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#ff69b4' }}>
                    UGX {disbursement.amount.toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {new Date(disbursement.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label="Completed" 
                      size="small" 
                      sx={{ 
                        bgcolor: '#4caf50', 
                        color: '#fff',
                        fontWeight: 600
                      }} 
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {disbursements.length > 10 && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#666' }}>
              Showing 10 of {disbursements.length} transactions
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default PartnerDisbursements;